import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { resolveUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowUpRight, Clock } from "lucide-react";

function BlogCardSkeleton() {
  return (
    <div className="glass rounded-3xl p-2">
      <Skeleton className="h-52 w-full rounded-2xl" />
      <div className="p-5 space-y-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-6 w-4/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blogs").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 py-10" data-testid="blog-page">
      <Seo
        title="Blog"
        description="Articles on frontend engineering, design systems, React, and full-stack MERN development."
        path="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Sandeep Varikuppala's Blog",
          blogPost: posts.map((p) => ({ "@type": "BlogPosting", headline: p.title, datePublished: p.created_at })),
        }}
      />
      <Reveal>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Writing</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-light tracking-tighter mt-3 max-w-3xl leading-[1.05]">
          Thoughts on craft & <span className="italic font-semibold">code</span>.
        </h1>
      </Reveal>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => <BlogCardSkeleton key={i} />)
          : posts.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                data-testid={`blog-card-${p.slug}`}
                className="h-full"
              >
                <Link to={`/blog/${p.slug}`} className="group block glass rounded-3xl p-2 hover:-translate-y-1 transition-transform duration-300 h-full flex flex-col">
                  <div className="relative h-52 overflow-hidden rounded-2xl shrink-0">
                    <img
                      src={resolveUrl(p.cover_image)}
                      alt={p.title}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5 flex-grow flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono">
                        <span>{new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                        {p.read_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.read_time}</span>}
                      </div>
                      <h2 className="font-heading text-xl mt-2 leading-snug group-hover:underline decoration-1 underline-offset-4">{p.title}</h2>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-2">{p.excerpt}</p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {(p.tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[11px] font-mono px-2 py-1 rounded-md bg-secondary text-secondary-foreground">{t}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
      </div>
      {!loading && posts.length === 0 && <p className="text-muted-foreground mt-10">No posts yet — check back soon.</p>}
    </div>
  );
}
